import { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Navigate, useLocation, useParams } from 'react-router';
import _ from 'lodash';
import dayjs from 'dayjs';

import useSurveyDrafts from '@/hooks/useSurveyDrafts';
import { useAppSelector } from '@/redux/hooks';
import usePatientDates from '@/patient-app-common/hooks/usePatientDates';
import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import useUnloadAlert from '@/hooks/useUnloadAlert';
import { useGetSurveyQuery } from '@/patient-app-common/api/surveyApi';
import { NoMatch } from '@/components/layout/NoMatch';

import { Survey } from './Survey';
import { SurveyFlat } from './SurveyFlat';
import { SurveyLoader } from './layout/SurveyLoader';
import { SurveyRecord } from './layout/SurveyRecord';

export function SurveyProvider() {
  useUnloadAlert();
  const { surveyId } = useParams();
  const { state } = useLocation();
  const eventId = _.toNumber(state?.eventId) || null;

  const { providerMode } = useAppSelector(({ auth }) => auth);

  const { data: patient } = useGetPatientQuery();
  const { unread } = useAppEvents({ skip: !eventId });
  const { patientDatesInMS } = usePatientDates();

  const id = useMemo(() => {
    if (!patient) return 0;

    if (surveyId === 'daily') {
      return patient.mainTrackerId;
    }

    return _.toNumber(surveyId);
  }, [patient, surveyId]);

  const { data, error } = useGetSurveyQuery(id, {
    skip: !patient || !surveyId,
  });
  const { expireDraft, parseDraftData } = useSurveyDrafts();

  const [hasEntry, setHasEntry] = useState(null);

  const [draft, setDraft] = useState(null);
  const [isDraftLoading, setIsDraftLoading] = useState(false);

  const idempotencyKey = useRef(crypto.randomUUID());
  const startTimestamp = useRef(null);
  const draftParsedRef = useRef(false);

  useEffect(() => {
    startTimestamp.current = _.now();
  }, []);

  const surveyData = useMemo(() => {
    if (data?.id) return data;
    return null;
  }, [data]);

  useEffect(() => {
    if (_.isEmpty(surveyData) || hasEntry !== null) return;

    const oneTimeSurvey =
      surveyData.editable || surveyData.setupSurvey || surveyData.mainSurvey;
    const lastEntryDate = surveyData.lastEntryDate;

    if (!lastEntryDate) {
      setHasEntry(false);
      return;
    }

    if (oneTimeSurvey) {
      setHasEntry(true);
      return;
    }

    if (surveyData.mainTracker) {
      setHasEntry(dayjs(lastEntryDate).isSame(dayjs(), 'day'));
      return;
    }

    if (eventId && unread?.length) {
      const unreadEventIds = _.map(unread, 'id');
      setHasEntry(!unreadEventIds.includes(eventId));
      return;
    }

    setHasEntry(false);
  }, [eventId, unread, surveyData]);

  useEffect(() => {
    if (_.isEmpty(surveyData)) return;

    Sentry.setContext('Params', {
      procedure_id: surveyData.id,
    });
  }, [eventId, surveyData]);

  useEffect(() => {
    if (_.isEmpty(surveyData?.draft) || !patient || !patientDatesInMS) return;
    if (surveyData?.draft.draftCreatedAt > startTimestamp.current) return;
    if (draftParsedRef.current) return;

    draftParsedRef.current = true;

    const { profile } = patient;

    const setDraftState = async () => {
      const expired = expireDraft(surveyData, unread);

      if (!expired) {
        setIsDraftLoading(true);
        try {
          const draftData = await parseDraftData(
            surveyData,
            patientDatesInMS,
            profile
          );
          setDraft(draftData);
        } finally {
          setIsDraftLoading(false);
        }
      }
    };

    setDraftState();
  }, [surveyData]);

  if (!surveyId) {
    return <Navigate to="/" replace />;
  }

  if (isDraftLoading || _.isEmpty(surveyData)) {
    return <SurveyLoader />;
  }

  if (error?.status === 404 || !patient) {
    return <NoMatch />;
  }

  if (surveyData.flattened) {
    return (
      <SurveyFlat
        eventId={eventId}
        idempotencyKey={idempotencyKey}
        patient={patient}
        patientDatesInMS={patientDatesInMS}
        surveyData={surveyData}
      />
    );
  }

  if (hasEntry) {
    return (
      <SurveyRecord
        editable={surveyData.editable}
        handleRedo={() => setHasEntry(false)}
        lastEntryDate={surveyData.lastEntryDate}
      />
    );
  }

  return (
    <>
      {providerMode && (
        <div className="p-4 bg-warning-300">
          <p className="text-xl">
            Filling out survey on behalf of {patient.firstName}{' '}
            {patient.lastName}
          </p>
        </div>
      )}
      <Survey
        draft={draft}
        eventId={eventId}
        idempotencyKey={idempotencyKey}
        patient={patient}
        patientDatesInMS={patientDatesInMS}
        surveyData={surveyData}
        startTimestamp={startTimestamp}
      />
    </>
  );
}
